/**
 * FAST calc UI labels (TitleCase in CompCalculatorSection) ↔ BE enum keys
 * (lowercase in advisor-comp.js / UpsertAdvisorAgreementInput).
 */

export const COMPANY_STAGE_LABEL_TO_KEY: Record<string, string> = {
  'Pre-seed': 'idea',
  Seed: 'startup',
  'Series A': 'growth',
}

export const CONTRIBUTION_TIER_LABEL_TO_KEY: Record<string, string> = {
  Standard: 'standard',
  Strategic: 'strategic',
  Expert: 'expert',
}

export const CALIBER_LABEL_TO_KEY: Record<string, string> = {
  Operator: 'operator',
  Executive: 'executive',
  Notable: 'notable',
}

export const WARM_INTRO_CALC_LABEL_TO_KEY: Record<string, string> = {
  None: 'none',
  Customers: 'customers',
  Investors: 'investors',
  Both: 'both',
}

export const COMPANY_STAGE_KEY_TO_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(COMPANY_STAGE_LABEL_TO_KEY).map(([label, key]) => [key, label])
)

export const CONTRIBUTION_TIER_KEY_TO_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(CONTRIBUTION_TIER_LABEL_TO_KEY).map(([label, key]) => [key, label])
)

export const CALIBER_KEY_TO_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(CALIBER_LABEL_TO_KEY).map(([label, key]) => [key, label])
)

export const WARM_INTRO_KEY_TO_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(WARM_INTRO_CALC_LABEL_TO_KEY).map(([label, key]) => [key, label])
)

/** Default FAST vesting (mirrors BE advisor-comp.js). */
export const AGREEMENT_VESTING_MONTHS_DEFAULT = 24
export const AGREEMENT_CLIFF_MONTHS_DEFAULT = 3
export const AGREEMENT_TERM_MONTHS_DEFAULT = 24

export const AGREEMENT_STATUS_DRAFT = 'draft'
export const AGREEMENT_STATUS_SENT = 'sent'
export const AGREEMENT_STATUS_SIGNED = 'signed'
export const AGREEMENT_STATUS_DECLINED = 'declined'
export const AGREEMENT_STATUS_SUPERSEDED = 'superseded'
export const AGREEMENT_STATUS_VOIDED = 'voided'

const STATUS_TITLES: Record<string, string> = {
  [AGREEMENT_STATUS_DRAFT]: 'Draft',
  [AGREEMENT_STATUS_SENT]: 'Pending Signature',
  [AGREEMENT_STATUS_SIGNED]: 'Signed',
  [AGREEMENT_STATUS_DECLINED]: 'Declined',
  [AGREEMENT_STATUS_SUPERSEDED]: 'Superseded',
  [AGREEMENT_STATUS_VOIDED]: 'Voided',
}

export const agreementStatusTitle = (statusKey?: string | null, fallback?: string | null) =>
  STATUS_TITLES[statusKey || ''] || fallback || statusKey || 'Unknown'

export const formatAgreementSigners = ({
  founderName,
  advisorName,
  founderHasSigned,
  advisorHasSigned,
}: {
  founderName?: string | null
  advisorName?: string | null
  founderHasSigned?: boolean | null
  advisorHasSigned?: boolean | null
}) => {
  const founder = founderName?.trim() || 'Founder'
  const advisor = advisorName?.trim() || 'Advisor'
  if (founderHasSigned && advisorHasSigned) {
    return `${founder} and ${advisor} signed`
  }
  if (founderHasSigned) {
    return `${founder} signed · waiting on ${advisor}`
  }
  if (advisorHasSigned) {
    return `${advisor} signed · waiting on ${founder}`
  }
  return `Waiting on ${founder} and ${advisor}`
}

export const formatEquityPercent = (n?: number | null) => {
  if (n == null || Number.isNaN(n)) {
    return '—'
  }
  return `${Number(n).toFixed(2)}%`
}

export const formatMonths = (n?: number | null, unit = 'mo') => {
  if (n == null || Number.isNaN(n)) {
    return '—'
  }
  return `${n}${unit}`
}

export const formatAgreementDate = (value?: string | null) => {
  if (!value) {
    return '—'
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {
    return value
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export const advisorDisplayName = (agreement: {
  advisorName?: string | null
  advisorEmail?: string | null
  advisorUser?: { fullName?: string | null; displayName?: string | null; email?: string | null } | null
}) =>
  agreement.advisorName?.trim() ||
  agreement.advisorUser?.fullName?.trim() ||
  agreement.advisorUser?.displayName?.trim() ||
  agreement.advisorEmail?.trim() ||
  agreement.advisorUser?.email?.trim() ||
  'Unnamed advisor'
